import React from 'react';
import { Button, Input, Popover } from 'antd';
import { BgColorsOutlined, DownOutlined } from '@ant-design/icons';
import { TAG_PREDEFINED_COLORS, generateRandomColor } from '../../constants/tagConstants';

type ColorPickerProps = {
	currentColor: string;
	onColorChange: (color: string) => void;
	onCancel: () => void;
	visible: boolean;
	onVisibleChange: (visible: boolean) => void;
	buttonDisplay?: React.ReactNode;
};

export const ColorPicker: React.FC<ColorPickerProps> = ({
	currentColor,
	onColorChange,
	onCancel,
	visible,
	onVisibleChange,
	buttonDisplay,
}) => {
	const [tempColor, setTempColor] = React.useState(currentColor);

	React.useEffect(() => {
		if (visible) {
			setTempColor(currentColor);
		}
	}, [visible, currentColor]);

	const handleApply = () => {
		onColorChange(tempColor);
	};

	const popoverContent = (
		<div className="w-64 p-3">
			<div className="mb-4">
				<div className="grid grid-cols-9 gap-1">
					{TAG_PREDEFINED_COLORS.map((color) => (
						<span
							key={color}
							onClick={() => setTempColor(color)}
							className={`h-6 w-6 cursor-pointer rounded border-2 transition-colors hover:border-gray-600 ${
								tempColor === color ? 'border-gray-800' : 'border-gray-300'
							}`}
							style={{ backgroundColor: color }}
							title={color}
						/>
					))}
				</div>
			</div>

			<div className="mb-4">
				<Button
					size="small"
					icon={<BgColorsOutlined />}
					onClick={() => setTempColor(generateRandomColor())}
					className="w-full"
				>
					Random
				</Button>
			</div>

			<div className="mb-4">
				<div className="flex items-center gap-2">
					<Input
						size="small"
						value={tempColor}
						onChange={(e) => setTempColor(e.target.value)}
						placeholder="#000000"
						className="flex-1"
						prefix={
							<span
								className="h-6 w-6 flex-shrink-0 rounded border border-gray-300"
								style={{ backgroundColor: tempColor }}
								title={tempColor}
							/>
						}
					/>
				</div>
			</div>

			<div className="flex gap-2">
				<Button size="small" type="primary" onClick={handleApply} className="flex-1">
					Apply
				</Button>
				<Button size="small" onClick={onCancel} className="flex-1">
					Cancel
				</Button>
			</div>
		</div>
	);

	return (
		<Popover
			content={popoverContent}
			title="Change Color"
			trigger="click"
			visible={visible}
			onVisibleChange={onVisibleChange}
		>
			{buttonDisplay ? (
				buttonDisplay
			) : (
				<Button className="shadow-sm">
					<span
						className="inline-block h-3 w-3 rounded-full"
						style={{ backgroundColor: currentColor }}
					/>
					<DownOutlined size={12} className="mt-px h-3 w-3 text-gray-600" />
				</Button>
			)}
		</Popover>
	);
};
